import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Logo } from "../components/Logo";
import { Star, Sparkles, Loader2, CheckCircle2, ExternalLink, Copy, Check, Heart, Wand2, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { REVIEW_SUGGESTIONS } from "../lib/suggestions";

export default function PublicReview() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [err, setErr] = useState(false);

  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [seedPhrase, setSeedPhrase] = useState(""); // tracks the chosen quick option for regeneration

  // UI state
  const [step, setStep] = useState("write"); // write | submitting | thanks-positive | thanks-negative
  const [generating, setGenerating] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Cache-buster to force fresh business config on every load — prevents stale Place IDs
    axios
      .get(`${API}/public/business/${slug}?ts=${Date.now()}`, { headers: { "Cache-Control": "no-cache" } })
      .then(({ data }) => {
        setBiz(data);
        // Debug logs (visible in browser devtools) — helps verify correct business mapping
        // eslint-disable-next-line no-console
        console.log("[ReviewAI] business slug:", slug);
        // eslint-disable-next-line no-console
        console.log("[ReviewAI] fetched business:", data?.name, "| place_id:", data?.google_place_id);
      })
      .catch(() => setErr(true));
  }, [slug]);

  if (err) return <Fallback text="Business not found." />;
  if (!biz) return <Fallback text="Loading…" />;

  // Generate full AI review from a short seed phrase (auto-runs on quick-option tap + Regenerate)
  const generate = async (seed, currentRating) => {
    if (!seed || !currentRating) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/public/review/${slug}/enhance`, {
        reviewer_name: name || "Customer",
        rating: currentRating,
        text: seed,
      });
      setText(data.enhanced || seed);
    } catch {
      // Fallback to the seed itself if AI fails
      setText(seed);
      toast.error("AI is busy, using your selection — feel free to edit.");
    } finally { setGenerating(false); }
  };

  const pickSuggestion = (s) => {
    setSeedPhrase(s);
    generate(s, rating);
  };

  const regenerate = () => {
    const seed = seedPhrase || text;
    if (!seed) { toast.error("Pick a quick option or write a few words first"); return; }
    generate(seed, rating);
  };

  const submit = async () => {
    if (!rating) { toast.error("Please pick a rating"); return; }
    if (!text.trim()) { toast.error("Please write or pick a review"); return; }
    setStep("submitting");
    try {
      const { data } = await axios.post(`${API}/public/review/${slug}`, {
        reviewer_name: name || "Customer",
        rating,
        text: text.trim(),
      });
      setSubmitResult(data);
      setStep(data.is_private ? "thanks-negative" : "thanks-positive");
    } catch {
      toast.error("Submit failed");
      setStep("write");
    }
  };

  const copyOnly = () => {
    const ok = copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Review copied successfully");
    } else {
      toast.error("Couldn't copy automatically — please long-press the review text to copy.");
    }
  };

  // Build the Google review URL directly from the saved Place ID.
  // Strict validation — must be a non-empty token with no whitespace, no slashes, no URL fragments.
  const rawPlaceId = (biz?.google_place_id || "").trim();
  const isValidPlaceId =
    rawPlaceId.length > 0 &&
    !/\s/.test(rawPlaceId) &&
    !rawPlaceId.includes("://") &&
    !/^https?/i.test(rawPlaceId) &&
    !rawPlaceId.includes("/") &&
    !rawPlaceId.includes("?") &&
    !rawPlaceId.includes("=");
  const googleReviewUrl = isValidPlaceId
    ? `https://search.google.com/local/writereview?placeid=${encodeURIComponent(rawPlaceId)}`
    : null;

  // Robust clipboard helper — modern API + execCommand fallback for older / mobile browsers
  const copyToClipboard = (txt) => {
    // Modern API (preferred, fires async but starts synchronously inside the click)
    let modernOk = false;
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      try {
        navigator.clipboard.writeText(txt).catch(() => {});
        modernOk = true;
      } catch { /* swallow */ }
    }
    // Legacy fallback — fully synchronous, works on Chrome desktop, mobile Safari/Chrome
    let legacyOk = false;
    try {
      const ta = document.createElement("textarea");
      ta.value = txt;
      ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none;";
      document.body.appendChild(ta);
      const sel = document.getSelection();
      const prev = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, txt.length);
      legacyOk = document.execCommand("copy");
      document.body.removeChild(ta);
      if (prev && sel) { sel.removeAllRanges(); sel.addRange(prev); }
    } catch { /* swallow */ }
    return modernOk || legacyOk;
  };

  const copyAndPost = () => {
    // Validate first — show explicit error if Place ID is missing or invalid.
    if (!googleReviewUrl) {
      toast.error("Business review configuration is incorrect.");
      return;
    }
    // eslint-disable-next-line no-console
    console.log("[ReviewAI] redirecting →", googleReviewUrl);
    // 1. Copy the latest AI-generated review text first, while user gesture is still active.
    const ok = copyToClipboard(text);
    if (ok) toast.success("Review copied successfully");
    else toast.error("Couldn't copy automatically — please long-press the review text to copy.");
    // 2. Mark this review as "redirected" in the dashboard so the owner can track it.
    if (submitResult?.review_id) {
      axios
        .post(`${API}/public/review/${slug}/${submitResult.review_id}/mark-redirected`)
        .catch(() => {});
    }
    // 3. Then redirect to Google review page in a new tab (gesture still valid in same handler).
    window.open(googleReviewUrl, "_blank");
  };

  return (
    <div className="min-h-screen text-white" data-testid="public-review-page">
      <header className="max-w-3xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/"><Logo /></Link>
        <span className="text-xs text-zinc-500">Secure form</span>
      </header>

      <section className="max-w-xl mx-auto px-6 pt-10 pb-20">
        {/* Business header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 bg-white/5 border border-white/10 text-xs text-zinc-300 mb-5">
            <Heart size={12} className="text-[#FF2D75]" /> Share your experience
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            How was your experience at <span className="gradient-text">{biz.name}</span>?
          </h1>
          {(biz.category || biz.location) && (
            <p className="text-sm text-zinc-500">
              {biz.category}{biz.category && biz.location ? " • " : ""}{biz.location}
            </p>
          )}
        </div>

        {(step === "write" || step === "submitting") && (
          <div className="glass p-7 space-y-6" data-testid="review-form-write">
            {/* Stars */}
            <div>
              <label className="text-xs text-zinc-400 mb-3 block text-center">Tap to rate</label>
              <div className="flex items-center justify-center gap-2" onMouseLeave={() => setHoverRating(0)}>
                {[1,2,3,4,5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseEnter={() => setHoverRating(i)}
                    onClick={() => setRating(i)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`star-${i}`}
                  >
                    <Star
                      size={42}
                      className={
                        (hoverRating || rating) >= i
                          ? "fill-[#FF2D75] text-[#FF2D75] drop-shadow-[0_0_12px_rgba(255,45,117,0.6)]"
                          : "text-zinc-700"
                      }
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">Your name <span className="text-zinc-600">(optional)</span></label>
              <input
                className="input-dark"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="reviewer-name-input"
              />
            </div>

            {/* Quick options — tap to auto-generate full AI review */}
            {rating > 0 && (
              <div className="space-y-2" data-testid="suggestion-pills">
                <div className="text-[10px] uppercase tracking-widest text-[#FF2D75] flex items-center gap-1.5">
                  <Wand2 size={10} /> Tap an option — AI writes the full review for you
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(REVIEW_SUGGESTIONS[rating] || []).map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => pickSuggestion(s)}
                      disabled={generating}
                      className={`text-left text-xs px-3 py-2.5 rounded-xl border transition disabled:opacity-50 ${
                        seedPhrase === s
                          ? "bg-[#FF2D75]/10 border-[#FF2D75]/40 text-white"
                          : "bg-white/[0.03] border-white/10 text-zinc-300 hover:bg-white/[0.06] hover:border-white/20"
                      }`}
                      data-testid={`suggestion-${rating}-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Review text — editable, with Regenerate */}
            {rating > 0 && (text || generating) && (
              <div data-testid="generated-review-block">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-zinc-400 flex items-center gap-1.5">
                    <Sparkles size={11} className="text-[#FF2D75]" /> Your review (edit if you'd like)
                  </label>
                  <button
                    type="button"
                    onClick={regenerate}
                    disabled={generating || !seedPhrase}
                    className="text-[11px] text-[#FF2D75] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="regenerate-btn"
                  >
                    {generating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                    Regenerate Review
                  </button>
                </div>
                <textarea
                  className="input-dark min-h-[140px] resize-y"
                  placeholder={generating ? "AI is writing your review…" : "Pick a quick option above to auto-generate."}
                  value={generating ? "" : text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={generating}
                  data-testid="reviewer-text-input"
                />
              </div>
            )}

            {/* Single primary action */}
            <button
              onClick={submit}
              disabled={generating || step === "submitting" || !rating || !text.trim()}
              className="btn-primary w-full justify-center"
              data-testid="submit-review-btn"
            >
              {step === "submitting" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              Submit Review
            </button>
          </div>
        )}

        {/* Positive thank-you (rating ≥ 3) */}
        {step === "thanks-positive" && (
          <div className="glass p-7 space-y-5" data-testid="thanks-positive">
            <div className="text-center">
              <CheckCircle2 className="text-[#FF2D75] mx-auto mb-3" size={42} />
              <h2 className="text-2xl font-semibold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Your review is ready!
              </h2>
              <p className="text-sm text-zinc-400">One tap to copy &amp; share it on Google.</p>
            </div>

            <div className="p-5 bg-white/[0.03] rounded-xl border border-white/5">
              <p className="text-sm text-zinc-200 leading-relaxed">"{text}"</p>
            </div>

            <div className="space-y-3">
              {googleReviewUrl ? (
                <>
                  <button onClick={copyAndPost} className="btn-primary w-full justify-center" data-testid="copy-and-post-btn">
                    <ExternalLink size={16} /> Copy &amp; Post on Google
                  </button>
                  <button
                    onClick={copyOnly}
                    className="text-xs text-zinc-400 hover:text-white inline-flex items-center justify-center gap-1.5 w-full"
                    data-testid="copy-only-btn"
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Only Copy Review"}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={copyOnly} className="btn-primary w-full justify-center" data-testid="copy-only-btn">
                    {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy Review"}
                  </button>
                  <p className="text-[11px] text-amber-400/80 text-center" data-testid="place-id-missing-note">
                    Business review configuration is incorrect.
                  </p>
                </>
              )}
              {submitResult?.website_url && (
                <a
                  href={submitResult.website_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-white/5 border border-white/10 hover:bg-white/10 transition text-sm font-medium w-full"
                  data-testid="visit-website-btn"
                >
                  Visit Website <ArrowRight size={14} />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Negative thank-you (rating ≤ 2) */}
        {step === "thanks-negative" && (
          <div className="glass p-10 text-center space-y-3" data-testid="thanks-negative">
            <CheckCircle2 className="text-[#FF2D75] mx-auto" size={42} />
            <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Thanks for your feedback
            </h2>
            <p className="text-sm text-zinc-400 max-w-sm mx-auto">
              We'll use this to improve. Your message has been sent privately to the owner.
            </p>
          </div>
        )}

        <p className="text-center text-[10px] text-zinc-600 mt-8">
          Powered by <Link to="/" className="hover:text-white">ReviewAI</Link>
        </p>
      </section>
    </div>
  );
}

const Fallback = ({ text }) => (
  <div className="min-h-screen grid place-items-center text-zinc-500">{text}</div>
);
